import './scratchpad-bucket.css';

import {
  ActionSheetButton,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  useIonActionSheet,
} from '@ionic/react';
import { checkmark } from 'ionicons/icons';
import { debounce } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePermissions,
  usePermissionsLazy,
} from '../../api/authorization.provider';
import {
  ListScratchpadBucketsInput,
  ScratchpadBucket,
  ScratchpadBucketScope,
  ScratchpadBucketType,
  useListScratchpadBucketsLazyQuery,
} from '../../api/graphql/generated';
import {
  trackCobotWelderAppliedBucketSettingsToPartSettings,
  trackCobotWelderLaunchedBucketWriter,
} from '../../config/analytics';
import { SystemUnit } from '../../scratchpad/application/shared/model/flow.types';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { useScratchpadServiceContext } from '../machine/scratchpad/scratchpad-service-context-provider';
import { Searchbar } from '../ui/searchbar/searchbar';

interface FindParams {
  searchText: string | undefined | null;
}

interface ScratchpadBucketExplorerProps {
  parentId: string;
  scratchpadAppSlug: string;
  scope: ScratchpadBucketScope;
  type: ScratchpadBucketType;
  selectionLabel?: string;
  viewLabel?: string;
  editLabel?: string;
  onSelection: (bucket: ScratchpadBucket) => void;
  onViewBucket?: (id: string) => void;
  searchPlaceholder?: string;
  system: SystemUnit;
  implicitSearchTags: Record<string, unknown>;
  getBucketSubheading: (bucket: ScratchpadBucket) => string | undefined;
  disableEdit?: boolean;
  disableView?: boolean;
}

export const ScratchpadBucketExplorer = (
  props: ScratchpadBucketExplorerProps
): ReactElement => {
  const {
    parentId,
    scratchpadAppSlug,
    scope,
    searchPlaceholder,
    onSelection,
    onViewBucket,
    selectionLabel,
    viewLabel,
    editLabel,
    system,
    implicitSearchTags,
    getBucketSubheading,
    type,
    disableEdit,
    disableView,
  } = props;

  const { i18n } = useTranslation();
  const initialLoading = useRef<boolean>(true);
  const infiniteScrollRef = useRef<HTMLIonInfiniteScrollElement>(null);
  const { scratchpad } = useScratchpadServiceContext();

  const [selectedBucket, setSelectedBucket] = useState<
    ScratchpadBucket | undefined
  >();

  const [presentBucketActions, _dismissBucketActions] = useIonActionSheet();
  const scratchpadPermissions = usePermissions('scratchpad', scratchpad);
  const getScratchpadBucketPermissions = usePermissionsLazy();

  const onBucketSelected = useCallback(
    (bucket: ScratchpadBucket) => {
      if (bucket) {
        const bucketPermissions = getScratchpadBucketPermissions(
          'scratchpad_bucket',
          bucket
        );

        const buttons: ActionSheetButton[] = [
          {
            text: i18n.t('common:cancel'),
            role: 'cancel',
          },
        ];

        if (scratchpadPermissions.update) {
          buttons.push({
            text: selectionLabel ?? i18n.t('common:scratchpad_bucket_use'),
            handler: () => {
              onSelection(bucket);
              trackCobotWelderAppliedBucketSettingsToPartSettings();
            },
          });
        }

        if (!disableView && bucketPermissions.read) {
          const permittedActionLabel =
            !disableEdit && bucketPermissions.update
              ? editLabel ?? i18n.t('common:scratchpad_bucket_edit')
              : viewLabel ?? i18n.t('common:scratchpad_bucket_view');

          buttons.push({
            text: permittedActionLabel,
            handler: () => {
              if (onViewBucket) {
                trackCobotWelderLaunchedBucketWriter();
                onViewBucket(bucket.id);
              }
            },
          });
        }

        presentBucketActions({
          onWillDismiss: () => {
            setSelectedBucket(undefined);
          },
          buttons,
          header: bucket.name as string,
        });
      }
    },
    [
      presentBucketActions,
      getScratchpadBucketPermissions,
      scratchpadPermissions.update,
      selectionLabel,
      viewLabel,
      editLabel,
      onSelection,
      onViewBucket,
      i18n,
      disableEdit,
      disableView,
    ]
  );

  useEffect(() => {
    if (selectedBucket) {
      onBucketSelected(selectedBucket);
    }
  }, [onBucketSelected, selectedBucket]);

  const [
    listScratchpadBuckets,
    { data: listScratchpadBucketsData, error, loading, fetchMore },
  ] = useListScratchpadBucketsLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [findParams, setFindParams] = useState<FindParams>({
    searchText: undefined,
  });

  const inputQueryRef = useRef<Partial<ListScratchpadBucketsInput>>({
    scratchpadAppSlug,
    parentId,
    scope,
    limit: 20,
    active: true,
    tags: implicitSearchTags,
    type,
  });

  const findScratchpadBuckets = useMemo(() => {
    return debounce((params: FindParams) => {
      listScratchpadBuckets({
        variables: {
          input: {
            ...inputQueryRef.current,
            query: params.searchText,
          },
        },
      });
      initialLoading.current = false;
    }, 500);
  }, [listScratchpadBuckets]);

  const totalItems =
    listScratchpadBucketsData?.listScratchpadBuckets.total ?? 0;
  const itemsLength =
    listScratchpadBucketsData?.listScratchpadBuckets.items?.length ?? 0;

  const getMore = async () => {
    if (fetchMore) {
      await fetchMore({
        variables: {
          input: {
            ...inputQueryRef.current,
            query: findParams.searchText,
            offset: itemsLength,
          },
        },
      });
    }
    infiniteScrollRef.current?.complete();
  };

  useEffect(() => {
    findScratchpadBuckets(findParams);
  }, [findParams, findScratchpadBuckets]);

  const results: ScratchpadBucket[] =
    listScratchpadBucketsData?.listScratchpadBuckets.items || [];

  const placeHolderText =
    searchPlaceholder ?? i18n.t('common:scratchpad_buckets_select');

  return (
    <div className="scratchpad-bucket-explorer-container">
      <Searchbar
        ionSearchbarProps={{
          placeholder: placeHolderText,
        }}
        onSearch={(searchText) => {
          setFindParams({ ...findParams, searchText });
        }}
      />
      <LoadingRetry
        loading={loading || initialLoading.current}
        error={error}
        results={{
          hasResults: results.length > 0,
          noResultsComponent: (
            <div className="content-center ion-padding">
              {i18n.t('common:scratchpad_buckets_no_results')}
            </div>
          ),
        }}
      >
        <ScratchpadBucketResultsList
          buckets={results}
          selectedBucket={selectedBucket}
          onBucketSelected={(bucket) => {
            setSelectedBucket(bucket);
          }}
          system={system}
          getBucketSubheading={getBucketSubheading}
        />
        <IonInfiniteScroll
          ref={infiniteScrollRef}
          onIonInfinite={getMore}
          className="ion-padding"
          disabled={totalItems === itemsLength}
          threshold={'25%'}
        >
          <IonInfiniteScrollContent loadingText={i18n.t('common:loading')} />
        </IonInfiniteScroll>
      </LoadingRetry>
    </div>
  );
};

interface ScratchpadBucketResultsList {
  buckets: ScratchpadBucket[];
  selectedBucket?: ScratchpadBucket;
  onBucketSelected: (bucket: ScratchpadBucket) => void;
  system: SystemUnit;
  getBucketSubheading: (bucket: ScratchpadBucket) => string | undefined;
}

const ScratchpadBucketResultsList = (
  props: ScratchpadBucketResultsList
): ReactElement => {
  const { buckets, selectedBucket, onBucketSelected, getBucketSubheading } =
    props;
  const { i18n } = useTranslation();

  const onSelect = (bucket: ScratchpadBucket): void => {
    onBucketSelected(bucket);
  };

  return (
    <IonList>
      <IonListHeader>{i18n.t('common:results')}</IonListHeader>
      {buckets.map((bucket) => {
        const subheading = getBucketSubheading(bucket);
        return (
          <IonItem
            key={bucket.id}
            lines="full"
            href=""
            onClick={(e) => {
              e.preventDefault();
              onSelect(bucket);
            }}
          >
            <IonLabel>
              {bucket.name} {subheading && <p>{subheading}</p>}
            </IonLabel>
            {selectedBucket?.id === bucket.id && (
              <IonIcon
                slot="end"
                icon={checkmark}
                color="primary"
                size="small"
              />
            )}
          </IonItem>
        );
      })}
    </IonList>
  );
};
